import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { FileText, Upload, Loader2, Trash2, Sparkles, GraduationCap, Target, Search, CheckCircle2, ExternalLink, Save, X } from "lucide-react";
import { toast } from "sonner";

function UploadCV({ isHr, myEid, employees, onDone }) {
  const [eid, setEid] = useState(isHr ? "" : (myEid || ""));
  const [busy, setBusy] = useState(false);
  const target = isHr ? eid : (myEid || "");
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!target) { toast.error("Välj medarbetare först"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("employee_id", target);
      fd.append("file", f, f.name);
      await api.post("/cv/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("CV uppladdat och tolkat av AI");
      onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="cv-upload-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Upload className="h-4 w-4 text-accent" />{isHr ? "Ladda upp CV" : "Mitt CV"}</div>
      <p className="text-xs text-muted-foreground">AI läser CV:t och drar ut sammanfattning, nyckelord, kompetenser (nivå 1–5) och certifikat. Ett CV per person – nytt ersätter det gamla. (pdf, docx, txt)</p>
      {isHr && (
        <div>
          <Label>Medarbetare / konsult</Label>
          <Select value={eid} onValueChange={setEid}>
            <SelectTrigger data-testid="cv-emp-select"><SelectValue placeholder="Välj person" /></SelectTrigger>
            <SelectContent>
              {(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="cv-file-label">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? "Tolkar CV…" : "Välj CV-fil"}
        <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onFile} disabled={busy} data-testid="cv-file-input" />
      </label>
    </Card>
  );
}

function CvCard({ cv, isHr, onChanged }) {
  const [applying, setApplying] = useState(false);
  const apply = async () => {
    setApplying(true);
    try {
      const skills = (cv.skills || []).map((s) => ({ name: s.name, level: s.level, competence_id: s.competence_id || null }));
      const certifications = (cv.certifications || []).map((c) => ({ name: c.name, issued_at: c.issued_at || null, expires_at: c.expires_at || null }));
      const r = await api.post("/cv/apply", { employee_id: cv.employee_id, skills, certifications });
      toast.success(`Tillämpat: ${r.data.levels_set} nivåer, ${r.data.competences_created} nya kompetenser, ${r.data.certs_created} certifikat`);
      onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };
  const del = async () => {
    if (!window.confirm("Ta bort CV:t?")) return;
    try { await api.delete(`/cv/${cv.employee_id}`); onChanged(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="p-4" data-testid={`cv-card-${cv.employee_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-accent" />
        <span className="font-semibold">{cv.employee_name}</span>
        {cv.years_experience != null && <Badge variant="secondary">{cv.years_experience} års erfarenhet</Badge>}
        {cv.file_path && <a href={`${API}/files/${cv.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`cv-open-${cv.employee_id}`}><ExternalLink className="h-3.5 w-3.5" />Öppna CV</a>}
        <Button size="icon" variant="ghost" className="ml-auto" onClick={del} data-testid={`cv-del-${cv.employee_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
      {cv.summary && <p className="mt-2 text-sm text-muted-foreground" data-testid={`cv-summary-${cv.employee_id}`}>{cv.summary}</p>}
      {(cv.keywords || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">{cv.keywords.map((k) => <span key={k} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">#{k}</span>)}</div>
      )}
      {(cv.skills || []).length > 0 && (
        <div className="mt-3">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5" />Kompetenser (AI-föreslagna nivåer)</div>
          <div className="flex flex-wrap gap-1.5">
            {cv.skills.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs" data-testid={`cv-skill-${cv.employee_id}-${i}`}>
                {s.name} <b className="text-accent">{s.level}</b>{!s.matched && <span className="text-amber-600" title="Ny kompetens">＋</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {(cv.certifications || []).length > 0 && (
        <div className="mt-3">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><GraduationCap className="h-3.5 w-3.5" />Certifikat / utbildningar</div>
          <div className="flex flex-wrap gap-1.5">{cv.certifications.map((c, i) => <Badge key={i} variant="outline" data-testid={`cv-cert-${cv.employee_id}-${i}`}>{c.name}{c.expires_at ? ` (t.o.m. ${c.expires_at})` : ""}</Badge>)}</div>
        </div>
      )}
      {isHr && (cv.skills || cv.certifications || []).length >= 0 && ((cv.skills || []).length + (cv.certifications || []).length) > 0 && (
        <Button className="mt-3" size="sm" onClick={apply} disabled={applying} data-testid={`cv-apply-${cv.employee_id}`}>
          {applying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}Tillämpa i kompetensmatrisen
        </Button>
      )}
    </Card>
  );
}

function MatchPanel() {
  const { data: comps } = useApi("/competences");
  const { data: templates, reload: reloadTpl } = useApi("/cv/templates");
  const [sel, setSel] = useState({});
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const toggle = (id) => setSel((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = 3; return n; });
  const saveTemplate = async () => {
    const required = Object.entries(sel).map(([competence_id, min_level]) => ({ competence_id, min_level }));
    if (!required.length) return toast.error("Välj minst en kompetens först");
    const name = window.prompt("Namn på uppdragsmall:");
    if (!name) return;
    try { await api.post("/cv/templates", { name, required, keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean) }); toast.success("Uppdragsmall sparad"); reloadTpl(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const loadTemplate = (tpl) => {
    setSel(Object.fromEntries((tpl.required || []).map((r) => [r.competence_id, r.min_level])));
    setKeywords((tpl.keywords || []).join(", "));
    toast.message(`Mall "${tpl.name}" laddad`);
  };
  const delTemplate = async (id) => { try { await api.delete(`/cv/templates/${id}`); reloadTpl(); } catch (e) { toast.error(apiErr(e)); } };
  const run = async () => {
    const required = Object.entries(sel).map(([competence_id, min_level]) => ({ competence_id, min_level }));
    if (!required.length) return toast.error("Välj minst en kompetens");
    setBusy(true);
    try {
      const r = await api.post("/cv/match", { required, keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean) });
      setRes(r.data.candidates || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="cv-match-panel">
      <div className="flex items-center gap-2 text-sm font-semibold"><Target className="h-4 w-4 text-accent" />Matcha mot uppdrag (strategisk kompetensförsörjning)</div>
      <p className="text-xs text-muted-foreground">Välj kravkompetenser och minsta nivå – systemet rangordnar konsulter utifrån kompetensmatris och CV. Spara återkommande uppdrag som mall.</p>
      {(templates || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="match-templates">
          <span className="text-xs text-muted-foreground">Uppdragsmallar:</span>
          {templates.map((tpl) => (
            <span key={tpl.id} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs" data-testid={`match-tpl-${tpl.id}`}>
              <button onClick={() => loadTemplate(tpl)} data-testid={`match-tpl-load-${tpl.id}`}>{tpl.name}</button>
              <button onClick={() => delTemplate(tpl.id)} data-testid={`match-tpl-del-${tpl.id}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-lg border p-2">
        {(comps || []).map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-sm" data-testid={`match-comp-${c.id}`}>
            <Checkbox checked={!!sel[c.id]} onCheckedChange={() => toggle(c.id)} />
            <span className="flex-1">{c.name}</span>
            {sel[c.id] && (
              <Select value={String(sel[c.id])} onValueChange={(v) => setSel((s) => ({ ...s, [c.id]: Number(v) }))}>
                <SelectTrigger className="h-7 w-24" data-testid={`match-level-${c.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>min {n}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        ))}
        {!(comps || []).length && <p className="text-xs text-muted-foreground">Inga kompetenser ännu.</p>}
      </div>
      <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Extra nyckelord i CV (valfritt, komma-separerat)" data-testid="match-keywords" />
      <div className="flex flex-wrap gap-2">
        <Button onClick={run} disabled={busy} data-testid="match-run">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Search className="mr-1 h-4 w-4" />}Hitta konsulter</Button>
        <Button variant="outline" onClick={saveTemplate} data-testid="match-save-template"><Save className="mr-1 h-4 w-4" />Spara som mall</Button>
      </div>
      {res && (
        <div className="space-y-2" data-testid="match-results">
          {res.length === 0 && <p className="text-sm text-muted-foreground">Inga kandidater.</p>}
          {res.map((c) => (
            <div key={c.employee_id} className="rounded-lg border p-3" data-testid={`match-cand-${c.employee_id}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium">{c.employee_name}</span>
                <Badge variant="secondary">poäng {c.score}</Badge>
                {c.has_cv && <Badge variant="outline"><FileText className="mr-1 h-3 w-3" />CV</Badge>}
                {!c.available && <Badge variant="outline" className="text-amber-700">upptagen</Badge>}
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                {c.matched.map((m, i) => <span key={i} className={`rounded px-1.5 py-0.5 ${m.partial ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{m.name} {m.level}/{m.required}</span>)}
                {c.missing.map((m, i) => <span key={i} className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">saknar {m.name} ({m.required})</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function CVPage() {
  const { data, reload } = useApi("/cv");
  const isHr = !!data?.is_hr;
  const { data: employees } = useApi(isHr ? "/employees" : null);
  const cvs = data?.cvs || [];
  return (
    <div className="mx-auto max-w-3xl" data-testid="cv-page">
      <PageHeader title="CV" icon={FileText}
        subtitle={isHr ? "Lagra och tolka CV:n så att all kompetens används rätt – koppla till matrisen och matcha mot uppdrag." : "Ladda upp och se ditt eget CV."} />
      <UploadCV isHr={isHr} myEid={data?.my_employee_id} employees={employees} onDone={reload} />
      {isHr && <MatchPanel />}
      {cvs.length === 0 ? (
        <EmptyState icon={FileText} title="Inga CV:n än" text={isHr ? "Ladda upp CV:n för dina medarbetare/konsulter." : "Ladda upp ditt CV ovan."} />
      ) : (
        <div className="space-y-3" data-testid="cv-list">
          {cvs.map((cv) => <CvCard key={cv.employee_id} cv={cv} isHr={isHr} onChanged={reload} />)}
        </div>
      )}
    </div>
  );
}
